import { useNavigate } from "react-router-dom";
import { useStore } from "../store/generalStore";
import { useMemo, useState } from "react";
import axios from "axios";
import Modal from "./Modal";
import logo from "../assets/feapi.png";
import { toast, Bounce } from "react-toastify";
import { Mail, Lock, User, ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setIsLogued } = useStore();

  const [isRegistered, setIsRegistered] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [formData, setFormData] = useState({
    usuario: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const isValidRegister = useMemo(() => {
    if (!isRegistered) return true;
    return (
      formData.usuario.trim().length >= 2 &&
      formData.password.length >= 6 &&
      formData.password === formData.confirmPassword
    );
  }, [isRegistered, formData]);

  const resetForm = () => {
    setFormData({
      usuario: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      if (isRegistered) {
        if (!isValidRegister) {
          toast.error("Revisá los datos (contraseña / confirmación).", {
            position: "top-right",
            autoClose: 4000,
            theme: "colored",
            transition: Bounce,
          });
          return;
        }

        const response = await axios.post(
          process.env.REACT_APP_SERVER_PATH + "registro",
          {
            user: formData.usuario,
            email: formData.email,
            password: formData.password,
          }
        );

        setData(response.data);
        setIsOpen(true);
        resetForm();
      } else {
        const response = await axios.post(
          process.env.REACT_APP_SERVER_PATH + "api/clientes/login",
          {
            email: formData.email,
            password: formData.password,
          }
        );

        if (response?.status === 200) {
          sessionStorage.setItem("_id", response.data.id);
          sessionStorage.setItem("user", response.data.user);
          setIsLogued(true);
          navigate("/home");
        } else {
          toast.error("No se pudo iniciar sesión.", {
            position: "top-right",
            theme: "colored",
            transition: Bounce,
          });
        }
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Ocurrió un error inesperado.";

      toast.error(msg, {
        position: "top-right",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        theme: "colored",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Registro correcto"
        message="¡Gracias! Te enviamos un correo con los siguientes pasos."
        confirmText="Cerrar"
        cancelText=""
        icono="1"
        onConfirm={() => setIsOpen(false)}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950 px-6 py-12">
        {/* Glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-red-500/10 blur-3xl" />
        </div>

        <div className="relative mx-auto flex w-full max-w-md flex-col items-center">
          {/* Brand */}
          <div className="mb-8 text-center">
<img
  alt="FEAPI"
  src={logo}
  className="mx-auto h-26 w-auto rounded-xl drop-shadow"
/>


            <h1 className="mt-4 text-2xl font-bold tracking-tight text-white">
              {isRegistered ? "Crear cuenta" : "Ingresar"}
            </h1>
            <p className="mt-1 text-sm text-white/70">
              {isRegistered
                ? "Registrate para acceder al panel."
                : "Ingresá con tu cuenta para continuar."}
            </p>
          </div>

          {/* Card */}
          <div className="w-full rounded-2xl border border-white/10 bg-white/5 p-6 shadow-xl backdrop-blur">
            {/* Tabs */}
            <div className="mb-6 grid grid-cols-2 rounded-xl bg-white/5 p-1">
              <button
                type="button"
                onClick={() => setIsRegistered(false)}
                className={[
                  "rounded-lg px-3 py-2 text-sm font-semibold transition",
                  !isRegistered
                    ? "bg-white text-slate-900"
                    : "text-white/70 hover:text-white",
                ].join(" ")}
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => setIsRegistered(true)}
                className={[
                  "rounded-lg px-3 py-2 text-sm font-semibold transition",
                  isRegistered
                    ? "bg-white text-slate-900"
                    : "text-white/70 hover:text-white",
                ].join(" ")}
              >
                Registro
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">
                  Email
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                  <Mail className="h-4 w-4 text-white/60" />
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">
                  Contraseña
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                  <Lock className="h-4 w-4 text-white/60" />
                  <input
                    name="password"
                    type="password"
                    required
                    autoComplete={isRegistered ? "new-password" : "current-password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                  />
                </div>

                {!isRegistered && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      className="text-xs font-semibold text-white/70 hover:text-white"
                      onClick={() =>
                        toast.info("Funcionalidad en progreso 👀", {
                          position: "top-right",
                          autoClose: 2000,
                          theme: "colored",
                          transition: Bounce,
                        })
                      }
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                )}
              </div>

              {/* Registro extra */}
              {isRegistered && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">
                      Confirmar contraseña
                    </label>
                    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                      <Lock className="h-4 w-4 text-white/60" />
                      <input
                        name="confirmPassword"
                        type="password"
                        required
                        autoComplete="new-password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                      />
                    </div>

                    {formData.confirmPassword.length > 0 &&
                      formData.password !== formData.confirmPassword && (
                        <p className="mt-2 text-xs text-red-300">
                          Las contraseñas no coinciden.
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-white/80">
                      Usuario
                    </label>
                    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                      <User className="h-4 w-4 text-white/60" />
                      <input
                        name="usuario"
                        type="text"
                        required
                        value={formData.usuario}
                        onChange={handleChange}
                        placeholder="Nicolás"
                        className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                      />
                    </div>
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading || (isRegistered && !isValidRegister)}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    {isRegistered ? "Crear cuenta" : "Ingresar"}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="pt-2 text-center text-xs text-white/60">
                Al continuar aceptás nuestros términos y políticas.
              </p>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-white/50">
            © {new Date().getFullYear()} FEAPI
          </p>
        </div>
      </div>
    </>
  );
}
